/**
 * Maps sidebar nav item ids → SaaS Foundation application module names.
 *
 * Access = subscription entitlement ∩ role grants from
 * GET /role-management/me/effective-access (not hardcoded personas).
 * Admins configure roles → modules in Settings → Role Management;
 * plans configure subscription.sidebar[agentic_rcm] allowlists.
 */

/** Always reachable after login (landing + operational home). */
export const HOME_NAV_IDS = new Set(['dashboard', 'dashboard-operational']);

/** Dashboard views gated by subscription + role (not ALWAYS_ON). */
export const GATED_DASHBOARD_NAV_IDS = new Set([
  'dashboard-executive',
  'dashboard-finance',
]);

export const SIDEBAR_PRODUCT_KEY = 'agentic_rcm';

export const ALWAYS_ON_NAV_IDS = new Set([
  ...HOME_NAV_IDS,
  'settings',
  'contact-us',
  'subscription',
]);

export function subscriptionAllowsNavItem(itemId, access, productKey = SIDEBAR_PRODUCT_KEY) {
  const allow = access?.subscription?.sidebar?.[productKey];
  if (!Array.isArray(allow) || allow.length === 0) return true;
  if (ALWAYS_ON_NAV_IDS.has(itemId)) return true;
  return allow.includes(itemId);
}

/** Nav item id → application name(s); user needs at least one granted application. */
export const NAV_ITEM_APPLICATIONS = {
  dashboard: ['agentic_rcm', 'agentic_rcm_agency'],
  'dashboard-executive': ['analytics'],
  'dashboard-finance': ['analytics'],
  'dashboard-operational': [
    'agentic_rcm',
    'agentic_rcm_agency',
    'denial_management',
    'claims_management',
    'charge_capture',
  ],
  'work-queue': [
    'denial_management',
    'claims_management',
    'charge_capture',
    'prior_auth',
    'insurance_verification',
  ],
  denials: ['denial_management'],
  claims: ['claims_management'],
  charges: ['charge_capture'],
  'medical-codes': ['claims_management', 'charge_capture'],
  'ar-posting': ['ar_posting'],
  eob: ['claims_management', 'ar_posting'],
  insurance: ['insurance_verification'],
  'prior-auth': ['prior_auth'],
  clearinghouse: ['claims_management'],
  'patient-billing': ['patient_management', 'ar_posting'],
  compliance: ['agentic_rcm', 'agentic_rcm_agency'],
  analytics: ['analytics'],
  reporting: ['analytics'],
  'agent-studio': ['agentic_rcm', 'agentic_rcm_agency'],
  integrations: ['agentic_rcm', 'agentic_rcm_agency'],
  'practice-data': ['agentic_rcm', 'agentic_rcm_agency'],
  'x12-pipeline': ['agentic_rcm', 'agentic_rcm_agency', 'analytics'],
  activity: ['agentic_rcm', 'agentic_rcm_agency'],
  'contract-management': ['agentic_rcm', 'agentic_rcm_agency'],
  'patient-portal': ['patient_management'],
  settings: ['settings', 'agentic_rcm', 'agentic_rcm_agency'],
  team: ['team_management'],
  subscription: ['billing_subscription'],
  'contact-us': [],
};

export const ROUTE_NAV_ID = {
  '/': 'dashboard',
  '/dashboard': 'dashboard',
  '/dashboard/executive': 'dashboard-executive',
  '/dashboard/finance': 'dashboard-finance',
  '/dashboard/operational': 'dashboard-operational',
  '/work-queue': 'work-queue',
  '/denials': 'denials',
  '/claims': 'claims',
  '/charges': 'charges',
  '/medical-codes': 'medical-codes',
  '/ar-posting': 'ar-posting',
  '/eob': 'eob',
  '/insurance': 'insurance',
  '/prior-auth': 'prior-auth',
  '/clearinghouse': 'clearinghouse',
  '/patient-billing': 'patient-billing',
  '/compliance': 'compliance',
  '/analytics': 'analytics',
  '/reporting': 'reporting',
  '/agent-studio': 'agent-studio',
  '/integrations': 'integrations',
  '/practice-data': 'practice-data',
  '/x12-pipeline': 'x12-pipeline',
  '/activity': 'activity',
  '/contract-management': 'contract-management',
  '/patient-portal': 'patient-portal',
  '/settings': 'settings',
  '/team': 'team',
  '/subscription': 'subscription',
  '/contact-us': 'contact-us',
};

/** Routes that skip module RBAC (profile, notifications, dashboards, etc.). */
export const UNGUARDED_ROUTE_PREFIXES = [
  '/',
  '/dashboard',
  '/profile',
  '/notifications',
  '/tasks',
  '/help',
  '/team-queue-goals',
];

export const DEFAULT_HOME_ROUTE = '/dashboard/operational';

export function grantedApplicationNames(access) {
  return new Set((access?.applications || []).map((a) => a.name));
}

/**
 * True when Role Management assigned full access to the RCM platform shell app.
 * Used as optional shortcut when a role has every module via agentic_rcm/agency full.
 */
export function hasFullPlatformShellAccess(access) {
  if (!access?.applications?.length) return false;
  return access.applications.some(
    (a) =>
      (a.name === 'agentic_rcm' || a.name === 'agentic_rcm_agency') &&
      (a.access_level === 'full' || a.access_level === 'admin')
  );
}

export function canAccessNavItem(itemId, access) {
  if (HOME_NAV_IDS.has(itemId)) return true;
  if (!access || access.loading) return false;
  if (!subscriptionAllowsNavItem(itemId, access)) return false;

  if (hasFullPlatformShellAccess(access)) return true;

  const required = NAV_ITEM_APPLICATIONS[itemId];
  if (!required || required.length === 0) return true;

  const granted = grantedApplicationNames(access);
  if (granted.size === 0) return false;

  return required.some((app) => granted.has(app));
}

export function filterNavigationItems(items, access) {
  if (!access || access.loading) return items;
  const entitled = items.filter((item) => subscriptionAllowsNavItem(item.id, access));
  const granted = grantedApplicationNames(access);
  if (granted.size === 0) {
    return entitled.filter((item) => HOME_NAV_IDS.has(item.id) || item.id === 'contact-us' || item.id === 'settings');
  }
  if (hasFullPlatformShellAccess(access)) return entitled;
  return entitled.filter((item) => canAccessNavItem(item.id, access));
}

export function resolveNavIdForPath(pathname, search = '') {
  if (UNGUARDED_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return null;
  }
  const moduleParam = new URLSearchParams(search || '').get('module');
  if (pathname === '/practice-data' && moduleParam === 'x12-pipeline') {
    return 'x12-pipeline';
  }
  const entries = Object.entries(ROUTE_NAV_ID).sort((a, b) => b[0].length - a[0].length);
  const match = entries.find(([path]) => pathname === path || pathname.startsWith(`${path}/`));
  return match ? match[1] : null;
}

export function getDefaultAccessibleRoute() {
  return DEFAULT_HOME_ROUTE;
}

export function isExecutiveOrgRole(role) {
  const r = String(role || '').toLowerCase();
  return r.includes('executive') || r.includes('c-suite');
}
